import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import logo from "../../Assets/logo.png";

const OrderHeaderLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleLogout = () => {
    logout();
    navigate("/background/gate/login");
  };

  const goToAdmin = () => {
    navigate("/adminstration/menu");
  };

  const openOrdersScreen = () => {
    window.open(`${window.location.origin}/orders`, "_blank", "noopener,noreferrer");
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        color: "text.primary",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.07)}`,
        backdropFilter: "blur(8px)",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 1.5, sm: 2.5 },
          minHeight: { xs: 56, sm: 64 },
          gap: 1,
        }}
      >
        {/* Logo + Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              flexShrink: 0,
            }}
          >
            <img
              src={logo}
              alt="Logo"
              style={{ width: "85%", height: "85%", objectFit: "contain" }}
            />
          </Box>
          {!isMobile && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                Nuvvy
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                شاشة الطلبات
              </Typography>
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1.25 },
            flexWrap: "nowrap",
          }}
        >
          {/* Admin dashboard */}
          <Button
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            startIcon={!isMobile && <DashboardIcon />}
            onClick={goToAdmin}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: { xs: "0.72rem", sm: "0.83rem" },
              px: { xs: 1.25, sm: 2 },
              borderColor: alpha(theme.palette.primary.main, 0.4),
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            {isMobile ? <DashboardIcon fontSize="small" /> : "لوحة التحكم"}
          </Button>

          {/* Orders screen */}
          <Button
            variant="contained"
            size={isMobile ? "small" : "medium"}
            startIcon={!isMobile && <OpenInNewIcon />}
            onClick={openOrdersScreen}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: { xs: "0.72rem", sm: "0.83rem" },
              px: { xs: 1.25, sm: 2 },
              whiteSpace: "nowrap",
              background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
              boxShadow: `0 3px 10px ${alpha(theme.palette.warning.main, 0.4)}`,
              "&:hover": {
                boxShadow: `0 5px 16px ${alpha(theme.palette.warning.main, 0.5)}`,
              },
            }}
          >
            {isMobile ? <OpenInNewIcon fontSize="small" /> : "شاشة الاستلام"}
          </Button>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, opacity: 0.5 }} />

          {/* User info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {!isMobile && (
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {user?.name}
                </Typography>
                <Chip
                  label="موظف"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, mt: 0.3 }}
                />
              </Box>
            )}
            <Avatar
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                bgcolor: "primary.main",
                fontWeight: 800,
                fontSize: "1rem",
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {user?.name?.charAt(0) || "A"}
            </Avatar>
          </Box>

          {/* Logout */}
          <Tooltip title="تسجيل الخروج" arrow>
            <IconButton
              onClick={handleLogout}
              aria-label="logout"
              size={isMobile ? "small" : "medium"}
              sx={{
                color: "error.main",
                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  borderColor: "error.main",
                },
              }}
            >
              <ExitToAppIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default OrderHeaderLayout;
